/* assets/sx-search-router.js
   -------------------------------------------------------------------------
   One search box, four destinations.

   Dave's ask: whatever a customer types into the search at the top of the
   page - or into the finder on the vehicle store - should land where it
   belongs, not always in the Shopify product search:

     EWN359          rego            -> vehicle store, rego lookup
     toyota          make            -> vehicle store, browse that make
     audi a4         make + model    -> vehicle store, that model's struts
     GS0014          our SKU         -> that product page
     B-330-75-1-1A   Struxa build    -> the build page, loaded with that build
     8mm x 280       everything else -> normal product search

   The hard case is a personalised plate made only of letters - ETHICL and
   BIGFLA are both real regos off Dave's test list, and neither looks any
   different from a word. Regex alone cannot separate "ETHICL" from "canopy".
   So anything alphabetic and plate-shaped that is NOT a known make is asked
   of Shopify's own predictive search first: if the shop has products for it,
   it is a product search; if the shop has nothing, it is treated as a rego.
   That check is one small request and only runs for the ambiguous case -
   every other route is decided synchronously with no network at all.

   Exposes:
     window.sxSearchClassify(q)  -> {type, ...} using sync rules only
     window.sxSearchRoute(q)     -> Promise<url|null>, may consult Shopify
   ------------------------------------------------------------------------- */
(function () {
  'use strict';
  if (window.__sxSearchRouter) return;
  window.__sxSearchRouter = true;

  var STORE_URL = '/pages/vehicle-store';
  var BUILD_URL = '/pages/all-gas-struts';
  var SEARCH_URL = '/search';

  /* Makes come from struxa-data-nz index.json. Held as a literal rather than
     fetched, because this has to decide inside a form submit and the list
     changes about once a year. Vehicle makes and the canopy brands the finder
     groups under "Canopy" are both here - a customer searching "arb" wants
     the canopy list, not a product grid. */
  var MAKES = ['Alfa Romeo','Arb','Audi','Austin','BMW','Brahma','Chevrolet','Chrysler','Citroen','Crown','Dacia','Daewoo','Daihatsu','Dodge','Explorer','Fiat','Ford','Holden','Honda','Hyundai','Infiniti','Isuzu','Jaguar','Jeep','Kia','Lada','Lancia','Land Rover','Lexus','Lotus','Mazda','Mercedes-Benz','Mini','Mitsubishi','Nissan','Opel','Peugeot','Pontiac','Porsche','Range Rover','Renault','Rexton','Rover','Saab','Seat','Skoda','Smart','SsangYong','Subaru','Suzuki','Taylor','Tesla','Toyota','Tuff','Vauxhall','Volkswagen','Volvo','Canopy'];

  /* lowercase -> the make spelled exactly as struxa-data-nz spells it. The
     finder looks makes up by that key, so "ssangyong" has to come back as
     "SsangYong" and "bmw" as "BMW" - title-casing the input would miss both. */
  var MAKE_KEY = {};
  MAKES.forEach(function (m) { MAKE_KEY[m.toLowerCase()] = m; });

  /* What people actually type for a make. */
  var ALIASES = {
    'vw': 'Volkswagen', 'merc': 'Mercedes-Benz', 'mercedes': 'Mercedes-Benz',
    'benz': 'Mercedes-Benz', 'mercedes benz': 'Mercedes-Benz',
    'chevy': 'Chevrolet', 'landrover': 'Land Rover', 'land-rover': 'Land Rover',
    'rangerover': 'Range Rover', 'range-rover': 'Range Rover',
    'alfa': 'Alfa Romeo', 'alfa-romeo': 'Alfa Romeo', 'vdub': 'Volkswagen',
    'ssang yong': 'SsangYong', 'ssang-yong': 'SsangYong', 'beemer': 'BMW',
    'bimmer': 'BMW', 'toyata': 'Toyota', 'hyundia': 'Hyundai',
    'vauxhaul': 'Vauxhall', 'peugot': 'Peugeot', 'mitsi': 'Mitsubishi',
    'landcruiser': 'Toyota'
  };

  /* Shop vocabulary. A plate-shaped word on this list is a product search,
     never a rego - it stops "canopy", "bonnet" and "struts" being posted to
     the rego lookup. */
  var SHOP_WORDS = /^(strut|struts|gas|spring|springs|bonnet|hood|boot|trunk|tailgate|hatch|canopy|lid|toolbox|bracket|brackets|ball|balls|stud|studs|socket|sockets|clip|clips|fitting|fittings|end|ends|mount|mounts|nut|nuts|bolt|bolts|kit|kits|trade|price|pricing|shipping|freight|delivery|warranty|returns?|refund|contact|account|order|orders|invoice|guide|custom|newton|force|length|stroke|shaft|tube|marine|rv|trailer|caravan|ute|damper|dampers|shock|shocks|help|about|faq|blog|sale|new)$/i;

  function norm(q) { return String(q || '').trim().replace(/\s+/g, ' '); }

  /* B-330-75-1-1A - matches parseProductCode() in gas-struts-catalogue.js. */
  var RE_BUILD = /^[A-Da-d]-\d{2,4}-\d{1,3}-\d[Aa]?-\d[Aa]?$/;
  /* GS0014 and friends. The catalogue is GS + four digits and the sheet runs
     past GS5825, so accept 1-5 digits and pad - "gs14" means GS0014. */
  var RE_GS = /^GS\s?-?\s?(\d{1,5})$/i;
  /* A spec, not a plate: 650N, 8mm, 280, 300 mm. */
  var RE_SPEC = /^\d{1,4}\s?(n|mm|cm|kg|newton|newtons)?$/i;
  /* A single token that could be an NZ plate: 2-6 characters, letters and
     digits only. Covers EWN359, WG9727, ETHICL, BIGFLA, GQT403. */
  var RE_PLATE = /^[A-Za-z0-9]{2,6}$/;

  function isMake(s) {
    s = s.toLowerCase();
    return ALIASES[s] || MAKE_KEY[s] || null;
  }

  /* Longest-first, so "land rover discovery" resolves the two-word make. */
  function splitMakeModel(q) {
    var parts = q.split(' ');
    for (var take = Math.min(3, parts.length); take >= 1; take--) {
      var head = parts.slice(0, take).join(' ');
      var mk = isMake(head);
      if (mk) return { make: mk, model: parts.slice(take).join(' ') };
    }
    return null;
  }

  /* Sync classification. type is one of:
     build | sku | make | makemodel | rego | product | ambiguous | empty  */
  function classify(raw) {
    var q = norm(raw);
    if (!q) return { type: 'empty', q: q };

    if (RE_BUILD.test(q)) return { type: 'build', q: q.toUpperCase() };

    var gs = q.match(RE_GS);
    if (gs) {
      var digits = gs[1];
      while (digits.length < 4) digits = '0' + digits;
      return { type: 'sku', q: q, sku: 'GS' + digits };
    }

    /* A force or a length is a product search, never a rego. Without this,
       "650N" and "8mm" both look exactly like a personalised plate. */
    if (RE_SPEC.test(q)) return { type: 'product', q: q };

    var mm = splitMakeModel(q.toLowerCase());
    if (mm) {
      return mm.model
        ? { type: 'makemodel', q: q, make: mm.make, model: mm.model }
        : { type: 'make', q: q, make: mm.make };
    }

    if (q.indexOf(' ') === -1 && RE_PLATE.test(q)) {
      if (SHOP_WORDS.test(q)) return { type: 'product', q: q };
      /* Has a digit and is not one of ours -> a plate, near enough certainly.
         Pure letters is the ETHICL / BIGFLA case: ask Shopify. */
      if (/\d/.test(q)) return { type: 'rego', q: q.toUpperCase() };
      return { type: 'ambiguous', q: q };
    }

    /* "ABC 123" and "ABC-123" are how people actually write a plate, and both
       used to land here as a text search, because the plate branch above needs
       q to contain no space at all.

       Re-run the WHOLE classifier on the compacted string rather than testing
       RE_PLATE here, so every guard above still applies to it: "8 mm" compacts
       to "8mm" and RE_SPEC still claims it as a product, "gs 123" compacts to
       "gs123" and is still a SKU. Only a result that comes back genuinely a
       plate is accepted, so this widens what counts as a rego but can never
       steal a product search.

       The <= 6 cap is what keeps "toyota corolla" out - it compacts to 13
       characters. NZ plates are 6 at most.

       Bounded: the compacted string has no separator left, so the inner call
       cannot reach this branch again. */
    if (/^[a-z0-9]+[\s-]+[a-z0-9]+$/i.test(q)) {
      var compact = q.replace(/[\s-]+/g, '');
      if (compact.length <= 6) {
        var spaced = classify(compact);
        if (spaced.type === 'rego' || spaced.type === 'ambiguous') return spaced;
      }
    }

    return { type: 'product', q: q };
  }

  function urlFor(c) {
    switch (c.type) {
      case 'build':     return BUILD_URL + '?code=' + encodeURIComponent(c.q);
      case 'rego':      return STORE_URL + '?q=' + encodeURIComponent(c.q);
      case 'make':      return STORE_URL + '?q=' + encodeURIComponent(c.make);
      case 'makemodel': return STORE_URL + '?q=' + encodeURIComponent(c.make + ' ' + c.model);
      default:          return SEARCH_URL + '?q=' + encodeURIComponent(c.q);
    }
  }

  /* Shopify's own predictive search, used for two things: turning a GS SKU
     into its product URL, and deciding whether an alphabetic plate-shaped
     word is really a word. Never blocks longer than a second and a bit -
     a slow shop must not stall a search. */
  function suggest(q, limit) {
    return new Promise(function (resolve) {
      var done = false;
      var t = setTimeout(function () { if (!done) { done = true; resolve(null); } }, 1200);
      fetch('/search/suggest.json?q=' + encodeURIComponent(q) +
            '&resources[type]=product&resources[limit]=' + (limit || 1) +
            '&resources[options][fields]=title,product_type,variants.sku,tag')
        .then(function (r) { return r.json(); })
        .then(function (d) {
          if (done) return;
          done = true; clearTimeout(t);
          var r = d && d.resources && d.resources.results;
          resolve((r && r.products) || []);
        })
        .catch(function () { if (!done) { done = true; clearTimeout(t); resolve(null); } });
    });
  }

  function route(raw) {
    var c = classify(raw);
    if (c.type === 'empty') return Promise.resolve(null);

    if (c.type === 'sku') {
      return suggest(c.sku, 1).then(function (ps) {
        if (ps && ps.length) return ps[0].url;
        /* No such SKU in the shop. "GS5825" is a real sheet SKU that is not a
           published product, and "GS1234" is also a perfectly good NZ plate
           (two letters, four digits) - so this could be either. Do NOT guess:
           show the empty product search, where the zero-result hint below
           offers the rego lookup as one click. Silently running a rego lookup
           on a SKU answers "no vehicle found", which explains nothing. */
        return SEARCH_URL + '?q=' + encodeURIComponent(c.q);
      });
    }

    if (c.type === 'ambiguous') {
      return suggest(c.q, 1).then(function (ps) {
        /* null = the lookup failed or timed out. Fall back to the shop search,
           which is where an unrouted query has always gone. */
        if (ps === null || ps.length) return SEARCH_URL + '?q=' + encodeURIComponent(c.q);
        return STORE_URL + '?q=' + encodeURIComponent(c.q.toUpperCase());
      });
    }

    return Promise.resolve(urlFor(c));
  }

  window.sxSearchClassify = classify;
  window.sxSearchRoute = route;
  window.sxSearchUrlFor = urlFor;

  /* ---- header / theme search forms ------------------------------------- */

  function isSearchForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    if (form.hasAttribute('data-sx-no-route')) return false;
    var action = (form.getAttribute('action') || '').split('?')[0];
    return form.getAttribute('role') === 'search' ||
           action === SEARCH_URL || /\/search$/.test(action);
  }

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!isSearchForm(form)) return;
    var input = form.querySelector('input[name="q"]');
    if (!input) return;
    var raw = norm(input.value);
    if (!raw) return;

    var c = classify(raw);
    /* Leave an ordinary product search completely alone - same form, same
       submit, same Shopify behaviour, no interception, no delay. */
    if (c.type === 'product') return;

    e.preventDefault();
    var btn = form.querySelector('button[type="submit"]');
    if (btn) btn.setAttribute('aria-busy', 'true');
    route(raw).then(function (url) {
      if (url) window.location.assign(url);
      else form.submit();
    });
  }, true);

  /* ---- zero-result search page ----------------------------------------- */

  /* Rather than silently redirecting a results page (which would hijack a
     search the customer meant), offer the better destination when Shopify
     came back with nothing. */
  function emptyResultsHint() {
    if (!/\/search\b/.test(window.location.pathname)) return;
    var params = new URLSearchParams(window.location.search);
    var q = norm(params.get('q'));
    if (!q) return;

    var main = document.getElementById('MainContent') || document.querySelector('main');
    if (!main) return;
    /* Dawn renders the "no results" copy inside .template-search--empty. */
    if (!document.querySelector('.template-search--empty')) return;

    var c = classify(q);
    var target = null, label = null;
    if (c.type === 'build') { target = urlFor(c); label = 'That looks like a Struxa build code. Open the build'; }
    else if (c.type === 'rego' || c.type === 'ambiguous' ||
             (c.type === 'sku' && /^[A-Za-z]{2}\d{4}$/.test(q.replace(/[\s-]/g, '')))) {
      target = STORE_URL + '?q=' + encodeURIComponent(q.replace(/[\s-]/g, '').toUpperCase());
      label = 'Is that a rego? Look it up in the vehicle store';
    }
    else if (c.type === 'make' || c.type === 'makemodel') { target = urlFor(c); label = 'Find struts for your ' + (c.make || q); }
    if (!target) return;

    var box = document.createElement('div');
    box.className = 'sx-search-hint';
    box.innerHTML =
      '<a href="' + target + '">' +
      '<span>' + label.replace(/</g, '&lt;') + '</span>' +
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>' +
      '</a>';
    var style = document.createElement('style');
    style.textContent =
      '.sx-search-hint{max-width:760px;margin:18px auto;padding:0 16px;font-family:Assistant,"Helvetica Neue",Helvetica,sans-serif}' +
      '.sx-search-hint a{display:flex;align-items:center;justify-content:space-between;gap:14px;' +
      'padding:14px 18px;background:#fff;border:2px solid #17171a;border-left:6px solid #ff7a00;' +
      'color:#17171a;text-decoration:none;font-weight:800;font-size:14.5px}' +
      '.sx-search-hint a:hover{background:#ff7a00;border-color:#ff7a00;color:#fff}';
    document.head.appendChild(style);
    var anchor = document.querySelector('.template-search--empty');
    anchor.parentNode.insertBefore(box, anchor.nextSibling);
  }

  if (document.readyState !== 'loading') emptyResultsHint();
  else document.addEventListener('DOMContentLoaded', emptyResultsHint);
})();
